import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { LoginReturnTargetService } from './login-return-target.service';

// How long to wait for the user to load on first page load before treating the visitor as logged out
const userLoadTimeoutMilliseconds = 15000;

/**
 * Lets an authenticated user through. Anyone else is sent to the login page, which returns them
 * to the URL they asked for (with its query and fragment) once they have logged in.
 */
export const authGuard: CanActivateFn & CanActivateChildFn = (_route, state) =>
  requireAuthenticatedUser(state.url);

/** As {@link authGuard}, but also lets through a guest who has started without an account */
export const authOrGuestGuard: CanActivateFn = (_route, state) => {
  if (hasSavedGuest()) {
    return true;
  }
  return requireAuthenticatedUser(state.url);
};

function requireAuthenticatedUser(targetUrl: string): Observable<boolean | UrlTree> {
  const authService = inject(AuthService);
  const router = inject(Router);
  const loginReturnTargetService = inject(LoginReturnTargetService);

  const redirectToLogin = (): UrlTree => {
    loginReturnTargetService.remember(targetUrl);
    return router.createUrlTree(['/login']);
  };

  // Without a token the application never loads a user, so there is nothing to wait for
  if (!authService.token) {
    return of(redirectToLogin());
  }

  return authService.user.pipe(
    filter((user) => user !== undefined),
    take(1),
    map((user) => (user ? true : redirectToLogin())),
    timeout(userLoadTimeoutMilliseconds),
    catchError(() => of(redirectToLogin())),
  );
}

function hasSavedGuest(): boolean {
  try {
    const guest = JSON.parse(localStorage.getItem('guest'));
    return !!guest?.uuid;
  } catch {
    return false;
  }
}
